import React, { FC } from 'react'
import { CrewComplaint } from '../core/_requests'
import VesselLandingLink from '../../../shared/components/VesselLandingLink'
import SearchableSelect from '../../../shared/components/SearchableSelect'

type ComplaintFormValues = {
  hodMasterResponse: string
  hodMasterResponseDate: string
  onboardPreventRecurrence: string
  hodSignature: string
  masterSignature: string
  onboardReviewRole: '' | 'HOD' | 'MASTER'
  onboardResolved: '' | 'YES' | 'NO'
  officeResolved: '' | 'YES' | 'NO'
  officeActionTaken: string
  officeActionDate: string
  officePreventRecurrence: string
  officeWelfareOfficerSignature: string
  officeResolutionDate: string
  authoritiesActionTaken: string
  authoritiesActionDate: string
  authoritiesPreventRecurrence: string
  authoritiesWelfareOfficerSignature: string
  authoritiesResolutionDate: string
  officeRemarks: string
}

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const CONTROL_CLASS = 'form-control'
const LABEL_CLASS = 'form-label fw-semibold fs-6 mb-2 text-dark'

type ReviewMode = 'hod' | 'master' | 'office' | 'authorities'

type Props = {
  visible: boolean
  complaint: CrewComplaint | null
  mode: ReviewMode
  loginUsername: string
  form: ComplaintFormValues
  isSaving: boolean
  onClose: () => void
  onApprove: () => void
  onOpenAttachments: () => void
  onFieldChange: (field: keyof ComplaintFormValues, value: string | number | undefined) => void
}

const CrewComplaintReviewModal: FC<Props> = ({
  visible,
  complaint,
  mode,
  loginUsername,
  form,
  isSaving,
  onClose,
  onApprove,
  onOpenAttachments,
  onFieldChange,
}) => {
  if (!visible || !complaint) return null

  const isHodMode = mode === 'hod'
  const isMasterMode = mode === 'master'
  const isOfficeMode = mode === 'office'
  const isAuthoritiesMode = mode === 'authorities'

  const complaintRankName = String(complaint.rankName || '').toLowerCase()
  const isMasterComplaint = complaint.rankId === 1 || complaintRankName.includes('master')
  const onboardReviewRole = String(complaint.onboardReviewRole || '').toUpperCase()
  const hodResponseSnapshot = complaint.hodResponse || complaint.hodMasterResponse || ''
  const hodResponseDateSnapshot = complaint.hodResponseDate || complaint.hodMasterResponseDate || ''
  const masterResponseSnapshot = complaint.masterResponse || (onboardReviewRole === 'MASTER' ? complaint.hodMasterResponse || '' : '')
  const masterResponseDateSnapshot = complaint.masterResponseDate || (onboardReviewRole === 'MASTER' ? complaint.hodMasterResponseDate || '' : '')
  const hasOnboardSnapshot =
    Boolean(String(hodResponseSnapshot || '').trim()) ||
    Boolean(String(masterResponseSnapshot || '').trim()) ||
    complaint.onboardResolved === true ||
    complaint.onboardResolved === false ||
    Boolean((complaint.onboardReviewedBy || '').trim())
  const hasOfficeSnapshot =
    Boolean((complaint.officeActionTaken || '').trim()) ||
    Boolean((complaint.officeActionDate || '').toString().trim()) ||
    Boolean((complaint.officePreventRecurrence || '').trim()) ||
    Boolean((complaint.officeWelfareOfficerSignature || '').trim()) ||
    Boolean((complaint.officeResolutionDate || '').toString().trim()) ||
    Boolean((complaint.officeRemarks || '').trim()) ||
    complaint.officeResolved === true ||
    complaint.officeResolved === false

  const assignedHodLabel =
    complaint.assignedHodRole === 'CHIEF_OFFICER'
      ? 'Chief Officer'
      : complaint.assignedHodRole === 'CHIEF_ENGINEER'
        ? 'Chief Engineer'
        : '-'

  return (
    <div style={WRAP_STYLE} tabIndex={-1}>
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        style={{
          width: '70vw',
          maxWidth: '900px',
          maxHeight: '80vh',
        }}
      >
          <div className='d-flex align-items-center justify-content-between border-bottom px-5 py-4 flex-shrink-0'>
            <h5 className='modal-title text-dark m-0'>
              {isHodMode
                ? 'HOD Review - Crew Complaint'
                : isMasterMode
                  ? 'Review - Crew Complaint'
                  : isAuthoritiesMode
                    ? 'Authorities Review - Crew Complaint'
                    : 'Review - Crew Complaint'}
            </h5>
            <button type='button' className='btn-close m-0' onClick={onClose} disabled={isSaving} />
          </div>
          <div className='flex-grow-1 overflow-auto px-5 py-4'>
            <div className='border rounded p-3 mb-4 bg-light'>
              <div className='row g-3'>
                <div className='col-md-4'><strong>Seafarer:</strong> {complaint.seafarerName || complaint.crewName || '-'}</div>
                <div className='col-md-4'>
                  <strong>Vessel:</strong>{' '}
                  <VesselLandingLink
                    vesselId={complaint.vesselId ?? null}
                    vesselName={complaint.vesselName || '-'}
                    className='fw-semibold text-dark text-hover-primary'
                  />
                </div>
                <div className='col-md-4'><strong>Report Date:</strong> {complaint.reportDate || '-'}</div>
                <div className='col-md-4'><strong>Assigned HOD:</strong> {assignedHodLabel}</div>
              </div>
              <div className='mt-3'><strong>Complaint:</strong> {complaint.description || '-'}</div>
            </div>

            {isHodMode && (
              <>
                <div className='mb-4'>
                  <label className={LABEL_CLASS}>Onboard Response (HOD) <span className='text-danger'>*</span></label>
                  <textarea
                    className={CONTROL_CLASS}
                    rows={3}
                    value={form.hodMasterResponse}
                    onChange={(e) => onFieldChange('hodMasterResponse', e.target.value)}
                  />
                </div>

                <div className='row g-3 mb-4'>
                  <div className='col-md-4'>
                    <label className={LABEL_CLASS}>Response Date <span className='text-danger'>*</span></label>
                    <input
                      type='date'
                      className={CONTROL_CLASS}
                      value={form.hodMasterResponseDate}
                      onChange={(e) => onFieldChange('hodMasterResponseDate', e.target.value)}
                    />
                  </div>
                  <div className='col-md-4'>
                    <label className={LABEL_CLASS}>HOD Signature</label>
                    <input className={CONTROL_CLASS} value={form.hodSignature || loginUsername} disabled />
                  </div>
                  <div className='col-md-4'>
                    <label className={LABEL_CLASS}>Resolved Onboard? <span className='text-danger'>*</span></label>
                    <SearchableSelect
                      className='form-select'
                      value={form.onboardResolved}
                      onChange={(e) => onFieldChange('onboardResolved', e.target.value as 'YES' | 'NO' | '')}
                    >
                      <option value=''>Select</option>
                      <option value='YES'>Yes</option>
                      <option value='NO'>No</option>
                    </SearchableSelect>
                  </div>
                </div>

                <div className='mb-4'>
                  <label className={LABEL_CLASS}>Action to Prevent Recurrence (Onboard) <span className='text-danger'>*</span></label>
                  <textarea
                    className={CONTROL_CLASS}
                    rows={3}
                    value={form.onboardPreventRecurrence}
                    onChange={(e) => onFieldChange('onboardPreventRecurrence', e.target.value)}
                  />
                </div>

                {form.onboardResolved === 'NO' && (
                  <div className='alert alert-info py-2'>Not resolved by HOD. On approval it will move to master review.</div>
                )}
              </>
            )}

            {isMasterMode && (
              <>
                <div className='border rounded p-3 mb-4 bg-light'>
                  <div><strong>HOD Response:</strong> {hodResponseSnapshot || '-'}</div>
                  <div><strong>HOD Response Date:</strong> {hodResponseDateSnapshot || '-'}</div>
                  <div><strong>HOD Reviewed By:</strong> {complaint.onboardReviewedBy || '-'}</div>
                </div>

                <div className='mb-4'>
                  <label className={LABEL_CLASS}>Onboard Response (Master) <span className='text-danger'>*</span></label>
                  <textarea
                    className={CONTROL_CLASS}
                    rows={3}
                    value={form.hodMasterResponse}
                    onChange={(e) => onFieldChange('hodMasterResponse', e.target.value)}
                  />
                </div>

                <div className='row g-3 mb-4'>
                  <div className='col-md-4'>
                    <label className={LABEL_CLASS}>Response Date <span className='text-danger'>*</span></label>
                    <input
                      type='date'
                      className={CONTROL_CLASS}
                      value={form.hodMasterResponseDate}
                      onChange={(e) => onFieldChange('hodMasterResponseDate', e.target.value)}
                    />
                  </div>
                  <div className='col-md-4'>
                    <label className={LABEL_CLASS}>Master Signature</label>
                    <input className={CONTROL_CLASS} value={form.masterSignature || loginUsername} disabled />
                  </div>
                  <div className='col-md-4'>
                    <label className={LABEL_CLASS}>Resolved Onboard? <span className='text-danger'>*</span></label>
                    <SearchableSelect
                      className='form-select'
                      value={form.onboardResolved}
                      onChange={(e) => onFieldChange('onboardResolved', e.target.value as 'YES' | 'NO' | '')}
                    >
                      <option value=''>Select</option>
                      <option value='YES'>Yes</option>
                      <option value='NO'>No</option>
                    </SearchableSelect>
                  </div>
                </div>

                <div className='mb-4'>
                  <label className={LABEL_CLASS}>Action to Prevent Recurrence (Onboard) <span className='text-danger'>*</span></label>
                  <textarea
                    className={CONTROL_CLASS}
                    rows={3}
                    value={form.onboardPreventRecurrence}
                    onChange={(e) => onFieldChange('onboardPreventRecurrence', e.target.value)}
                  />
                </div>

                {form.onboardResolved === 'NO' && (
                  <div className='alert alert-info py-2'>Not resolved onboard. On approval it will move to office review.</div>
                )}
              </>
            )}

            {isOfficeMode && (
              <>
                {!isMasterComplaint && hasOnboardSnapshot && (
                  <div className='border rounded p-3 mb-4 bg-light'>
                    <div><strong>HOD Response:</strong> {hodResponseSnapshot || '-'}</div>
                    <div><strong>HOD Response Date:</strong> {hodResponseDateSnapshot || '-'}</div>
                    <div><strong>Master Response:</strong> {masterResponseSnapshot || '-'}</div>
                    <div><strong>Master Response Date:</strong> {masterResponseDateSnapshot || '-'}</div>
                    <div><strong>Onboard Resolved:</strong> {complaint.onboardResolved === true ? 'Yes' : complaint.onboardResolved === false ? 'No' : '-'}</div>
                    <div><strong>Onboard Reviewed By:</strong> {complaint.onboardReviewedBy || '-'}</div>
                  </div>
                )}

                <div className='row g-3 mb-4'>
                  <div className='col-md-6'>
                    <label className={LABEL_CLASS}>Resolved by Office? <span className='text-danger'>*</span></label>
                    <SearchableSelect
                      className='form-select'
                      value={form.officeResolved}
                      onChange={(e) => onFieldChange('officeResolved', e.target.value as 'YES' | 'NO' | '')}
                    >
                      <option value=''>Select</option>
                      <option value='YES'>Yes</option>
                      <option value='NO'>No</option>
                    </SearchableSelect>
                  </div>
                </div>

                <div className='mb-4'>
                  <label className={LABEL_CLASS}>Action Taken by Office <span className='text-danger'>*</span></label>
                  <textarea className={CONTROL_CLASS} rows={3} value={form.officeActionTaken} onChange={(e) => onFieldChange('officeActionTaken', e.target.value)} />
                </div>

                <div className='row g-3 mb-4'>
                  <div className='col-md-6'>
                    <label className={LABEL_CLASS}>Office Action Date <span className='text-danger'>*</span></label>
                    <input type='date' className={CONTROL_CLASS} value={form.officeActionDate} onChange={(e) => onFieldChange('officeActionDate', e.target.value)} />
                  </div>
                  <div className='col-md-6'>
                    <label className={LABEL_CLASS}>Office Resolution Date <span className='text-danger'>*</span></label>
                    <input type='date' className={CONTROL_CLASS} value={form.officeResolutionDate} onChange={(e) => onFieldChange('officeResolutionDate', e.target.value)} />
                  </div>
                </div>

                <div className='mb-4'>
                  <label className={LABEL_CLASS}>Action to Prevent Recurrence (Office) <span className='text-danger'>*</span></label>
                  <textarea className={CONTROL_CLASS} rows={3} value={form.officePreventRecurrence} onChange={(e) => onFieldChange('officePreventRecurrence', e.target.value)} />
                </div>

                <div className='mb-4'>
                  <label className={LABEL_CLASS}>Welfare Officer Signature (Office) <span className='text-danger'>*</span></label>
                  <input className={CONTROL_CLASS} value={form.officeWelfareOfficerSignature || loginUsername} disabled />
                </div>

                <div className='mb-4'>
                  <label className={LABEL_CLASS}>Office Remarks</label>
                  <textarea className={CONTROL_CLASS} rows={3} value={form.officeRemarks} onChange={(e) => onFieldChange('officeRemarks', e.target.value)} />
                </div>

                {form.officeResolved === 'NO' && (
                  <div className='alert alert-warning py-2'>Not resolved by office. On approval it will escalate to Flag / National / PSC authorities.</div>
                )}
              </>
            )}

            {isAuthoritiesMode && (
              <>
                {(hasOnboardSnapshot || hasOfficeSnapshot) && (
                  <div className='border rounded p-3 mb-4 bg-light'>
                    {hasOnboardSnapshot && (
                      <div className='mb-3'>
                        <div className='fw-semibold mb-1'>Onboard Snapshot</div>
                        <div><strong>HOD Response:</strong> {hodResponseSnapshot || '-'}</div>
                        <div><strong>HOD Response Date:</strong> {hodResponseDateSnapshot || '-'}</div>
                        <div><strong>Master Response:</strong> {masterResponseSnapshot || '-'}</div>
                        <div><strong>Master Response Date:</strong> {masterResponseDateSnapshot || '-'}</div>
                        <div><strong>Onboard Resolved:</strong> {complaint.onboardResolved === true ? 'Yes' : complaint.onboardResolved === false ? 'No' : '-'}</div>
                        <div><strong>Onboard Reviewed By:</strong> {complaint.onboardReviewedBy || '-'}</div>
                      </div>
                    )}

                    {hasOfficeSnapshot && (
                      <div>
                        <div className='fw-semibold mb-1'>Office Snapshot</div>
                        <div><strong>Office Action:</strong> {complaint.officeActionTaken || '-'}</div>
                        <div><strong>Office Action Date:</strong> {complaint.officeActionDate || '-'}</div>
                        <div><strong>Office Prevent Recurrence:</strong> {complaint.officePreventRecurrence || '-'}</div>
                        <div><strong>Office Welfare Signature:</strong> {complaint.officeWelfareOfficerSignature || '-'}</div>
                        <div><strong>Office Resolution Date:</strong> {complaint.officeResolutionDate || '-'}</div>
                        <div><strong>Office Resolved:</strong> {complaint.officeResolved === true ? 'Yes' : complaint.officeResolved === false ? 'No' : '-'}</div>
                        <div><strong>Office Remarks:</strong> {complaint.officeRemarks || '-'}</div>
                      </div>
                    )}
                  </div>
                )}

                <div className='mb-4'>
                  <label className={LABEL_CLASS}>Action by Flag / National / PSC Authorities <span className='text-danger'>*</span></label>
                  <textarea className={CONTROL_CLASS} rows={3} value={form.authoritiesActionTaken} onChange={(e) => onFieldChange('authoritiesActionTaken', e.target.value)} />
                </div>

                <div className='row g-3 mb-4'>
                  <div className='col-md-6'>
                    <label className={LABEL_CLASS}>Authorities Action Date <span className='text-danger'>*</span></label>
                    <input type='date' className={CONTROL_CLASS} value={form.authoritiesActionDate} onChange={(e) => onFieldChange('authoritiesActionDate', e.target.value)} />
                  </div>
                  <div className='col-md-6'>
                    <label className={LABEL_CLASS}>Authorities Resolution Date <span className='text-danger'>*</span></label>
                    <input type='date' className={CONTROL_CLASS} value={form.authoritiesResolutionDate} onChange={(e) => onFieldChange('authoritiesResolutionDate', e.target.value)} />
                  </div>
                </div>

                <div className='mb-4'>
                  <label className={LABEL_CLASS}>Action to Prevent Recurrence (Authorities) <span className='text-danger'>*</span></label>
                  <textarea className={CONTROL_CLASS} rows={3} value={form.authoritiesPreventRecurrence} onChange={(e) => onFieldChange('authoritiesPreventRecurrence', e.target.value)} />
                </div>

                <div className='mb-4'>
                  <label className={LABEL_CLASS}>Welfare Officer Signature (Authorities) <span className='text-danger'>*</span></label>
                  <input className={CONTROL_CLASS} value={form.authoritiesWelfareOfficerSignature || loginUsername} disabled />
                </div>
              </>
            )}

          </div>
          <div className='border-top d-flex justify-content-end gap-2 px-5 py-4 flex-shrink-0'>
            <button className='btn btn-light-primary btn-sm' onClick={onOpenAttachments} disabled={isSaving}>Attachments</button>
            <button className='btn btn-light btn-sm' onClick={onClose} disabled={isSaving}>Close</button>
            <button className='btn btn-success btn-sm' onClick={onApprove} disabled={isSaving}>
              {isSaving
                ? 'Processing...'
                : isHodMode
                  ? 'Complete HOD Review'
                  : isMasterMode
                    ? 'Complete Review'
                    : isAuthoritiesMode
                      ? 'Complete Authorities Review'
                      : 'Complete Review'}
            </button>
          </div>
      </div>
    </div>
  )
}

export default CrewComplaintReviewModal
